import {
  b2bProductionSettings,
  b2bStageSettings,
  developmentSettings,
} from '@twisto/environment'

type Runtime = 'production' | 'stage' | 'integration' | 'development'

export type EnvConfig = {
  region: GLOBALS.Region
  RUNTIME: Runtime
  MOCK_API: boolean
  AUTH?: (typeof b2bStageSettings)['AUTH']
  HOST: string
  BASE_DOMAIN: string
  STATIC_DOMAIN: string
  B2B_API_ENDPOINT: string
}

const protocol = 'http://'
const secureProtocol = 'https://'
const DEFAULT_REGION = 'CZ'
const LOCAL_STATIC_DOMAIN = 'http://twisto.top'

type HostParts = {
  hostname: string
  host: string
  port?: string
}

type B2bPortal = {
  apiEndpoint: string
  auth: NonNullable<EnvConfig['AUTH']>
  runtime: Runtime
  staticDomain: string
}

const B2B_PORTALS: Record<string, B2bPortal | undefined> = {
  'pro.twistoapp.com': {
    apiEndpoint: b2bProductionSettings.B2B_API_ENDPOINT,
    auth: b2bProductionSettings.AUTH,
    runtime: 'production',
    staticDomain: 'https://static.twistopay.com',
  },
  'pro.twisto.live': {
    apiEndpoint: b2bStageSettings.B2B_API_ENDPOINT,
    auth: b2bStageSettings.AUTH,
    runtime: 'stage',
    staticDomain: 'https://static.twisto.live',
  },
}

const parseHost = (host: string): HostParts => {
  const matches = /^(?:https?:\/\/)?([^:]+):?(\d+)?$/.exec(host)

  if (matches === null) {
    throw new Error('invalid host')
  }

  const [, hostname, port] = matches

  return {
    hostname,
    host: port ? `${hostname}:${port}` : hostname,
    port,
  }
}

const getBrowserHost = () =>
  typeof window === 'undefined' ? undefined : window.location.host

const getStaticDomain = ({ host, hostname, port }: HostParts) => {
  if (hostname.endsWith('twisto.top') && port === undefined) {
    return LOCAL_STATIC_DOMAIN
  }

  return `${protocol}${host}`
}

const getLocalApiEndpoint = ({ host, hostname, port }: HostParts) => {
  const { B2B_APP_PORT, B2B_MOCK_PORT } = developmentSettings

  if (hostname === 'localhost' || hostname.startsWith('192.')) {
    return `${protocol}${host.replace(
      B2B_APP_PORT.toString(),
      B2B_MOCK_PORT.toString()
    )}`
  }

  const baseDomain = port
    ? host.replace(B2B_APP_PORT.toString(), B2B_MOCK_PORT.toString())
    : hostname

  return `${protocol}api.${baseDomain.replace(/^www\./, '')}`
}

const getLocalRegion = (hostname: string): GLOBALS.Region => {
  const twistoTopMatch = /^(\w+)\.twisto\.top$/.exec(
    hostname.replace(/^www\./, '')
  )

  return (
    (twistoTopMatch?.[1].toUpperCase() as GLOBALS.Region | undefined) ??
    DEFAULT_REGION
  )
}

const getLocalRuntime = ({ hostname, port }: HostParts): Runtime =>
  hostname.endsWith('twisto.top') && port === undefined
    ? 'integration'
    : 'development'

const getLocalMockApi = ({ hostname, port }: HostParts) =>
  !hostname.endsWith('twisto.top') || port !== undefined

export const getEnvConfig = (hostArg?: string): EnvConfig => {
  const browserHost = getBrowserHost()
  const hostParts = parseHost(hostArg ?? browserHost ?? 'localhost')
  const b2bPortal = B2B_PORTALS[hostParts.hostname]

  if (b2bPortal) {
    return {
      region: DEFAULT_REGION,
      RUNTIME: b2bPortal.runtime,
      MOCK_API: false,
      AUTH: b2bPortal.auth,
      HOST: hostParts.host,
      BASE_DOMAIN: `${secureProtocol}${hostParts.host}`,
      STATIC_DOMAIN: b2bPortal.staticDomain,
      B2B_API_ENDPOINT: b2bPortal.apiEndpoint,
    }
  }

  return {
    region: getLocalRegion(hostParts.hostname),
    RUNTIME: getLocalRuntime(hostParts),
    MOCK_API: getLocalMockApi(hostParts),
    HOST: hostParts.host,
    BASE_DOMAIN: `${protocol}${hostParts.host}`,
    STATIC_DOMAIN: getStaticDomain(hostParts),
    B2B_API_ENDPOINT: getLocalApiEndpoint(hostParts),
  }
}

export const envConfig = getEnvConfig()
